// XSLT içe aktarımında dinamik alanları (xsl:value-of XPath'leri) ve tablo başlıklarını
// bizim alan kataloğumuza (fieldCatalog.SOURCE_FIELDS) eşler.
//
// İki sinyal kullanılır:
//  1) value-of select XPath'i (genelde UBL-TR yerel ad: cbc:UUID, cac:Item/cbc:Name, ...)
//  2) görünen Türkçe başlık metni ("Miktar", "Birim Fiyat", "Vergi No", ...)
// Türk e-Fatura XSLT'leri kolonları Türkçe etiketlediği için BAŞLIK metni çoğu zaman
// XPath'ten daha güveniliridir; bu yüzden detay kolonlarında başlık önce denenir.

import { SOURCE_FIELDS } from './fieldCatalog';

// Bir XPath/etiketin son anlamlı adımını sadeleştirir: ad alanı önekini ve {…} ekini at.
// ÖNEMLİ: önce toLowerCase, SONRA harf-dışı temizliği — yoksa "İ" küçükken birleşik nokta
// (U+0307) üretir ("i̇skonto") ve "iskonto" eşleşmesi bozulur.
function norm(str) {
  if (!str) return '';
  const s = String(str).replace(/[{}]/g, '').toLowerCase()
    .split('/').pop()           // son adım
    .split(':').pop()           // ad alanı önekini at (cbc:uuid → uuid)
    .replace(/\[.*?\]/g, '');   // yüklem [..]
  return s.replace(/[^a-zçğıöşü0-9]/g, '');
}

function fullLower(str) {
  return String(str || '').toLowerCase();
}

// Etiket/başlık metni için: XPath gibi BÖLME (/ ve : ile) — "İskonto/ Arttırım Oranı" gibi
// içinde eğik çizgi olan başlıklar bütün kalmalı. (toLowerCase önce — bkz. norm notu.)
function normLabel(str) {
  return String(str || '').replace(/[{}]/g, '').toLowerCase().replace(/[^a-zçğıöşü0-9]/g, '');
}

const fieldType = (source, key) =>
  (SOURCE_FIELDS[source]?.fields.find((f) => f.key === key) || {}).type || 'text';

// --- Başlık kaynağı (Müşteri / Belge) eşlemesi ---
// path: value-of select; label: yakındaki etiket metni (varsa).
export function mapHeaderField(path, label) {
  const p = fullLower(path);
  const n = norm(path);
  const l = normLabel(label);

  const isCustomer = /customer|buyer|musteri|müşteri|alici|alıcı|sayin|sayın/.test(p) || /müşteri|musteri|sayin|sayın|alici|alıcı|ünvan|unvan/.test(fullLower(label));

  // Müşteri alanları — özgül kontroller ÖNCE, jenerik "ad → Ünvan" EN SONA
  // (yoksa "StreetName" gibi içinde "name" geçen alanlar yanlışlıkla Ünvan'a düşer).
  if (isCustomer) {
    if (/streetname|address|adres|delivery|teslimat/.test(n) || /adres/.test(l)) return bind('Customer', 'Adress');
    if (/citysubdivision|district|mahalle|semt|ilce|ilçe/.test(n) || /mahalle|semt/.test(l)) return bind('Customer', 'District');
    if (/cityname|city|sehir|şehir|^il$/.test(n) || /şehir|sehir|^il$/.test(l)) return bind('Customer', 'City');
    if (/taxofficename|vergidaire/.test(n) || /vergi dairesi|vd/.test(fullLower(label))) return bind('Customer', 'TaxOffice');
    if (/companyid|taxnumber|vergino|vkn/.test(n) || /vergi no|vkn/.test(fullLower(label))) return bind('Customer', 'TaxNumber');
    if (/identity|tckimlik|tckn/.test(n) || /tckn|tc kimlik|kimlik/.test(fullLower(label))) return bind('Customer', 'IdentityNumber');
    if (/telephone|phone|telefon|gsm|cep/.test(n) || /telefon|gsm|cep/.test(l)) return bind('Customer', 'Phone');
    if (/electronicmail|email|eposta|mail/.test(n) || /e-?posta|email|mail/.test(fullLower(label))) return bind('Customer', 'Email');
    if (/partyname|name|unvan|ünvan|title|sayin|sayın/.test(n) || /unvan|ünvan|isim|name|sayın|sayin/.test(l)) return bind('Customer', 'Title');
    return bind('Customer', 'Title');
  }

  // Belge (Fatura/İrsaliye/Sipariş) alanları
  if (/uuid|ettn/.test(n) || /ettn/.test(l)) return bind('Invoice', 'Ettn');
  if (/issuedate|belgetarih|faturatarih|tarih/.test(n) || /tarih/.test(l)) return bind('Invoice', 'DocumentDate');
  if (/duedate|vade/.test(n) || /vade/.test(l)) return bind('Invoice', 'DueDate');
  if (/(invoice|fatura|belge)?(no|number)$/.test(n) || /fatura no|belge no|irsaliye no|no$/.test(fullLower(label))) return bind('Invoice', 'TextId');
  if (/payableamount|odenecek|ödenecek|geneltoplam|genel/.test(n) || /ödenecek|genel toplam/.test(fullLower(label))) return bind('Invoice', 'NetAmount');
  if (/taxinclusive|vergilitoplam/.test(n)) return bind('Invoice', 'NetAmount');
  if (/taxexclusive|matrah/.test(n) || /matrah/.test(l)) return bind('Invoice', 'VatBase');
  if (/lineextensionamount|malhizmettoplam|brut|brüt/.test(n) || /mal.*hizmet|brüt|brut/.test(fullLower(label))) return bind('Invoice', 'GrossAmount');
  if (/allowancetotal|iskonto|indirim/.test(n) || /iskonto|indirim/.test(l)) return bind('Invoice', 'DiscountAmount');
  if (/taxamount|kdvtutar|hesaplanankdv/.test(n) || /kdv tutar|hesaplanan kdv/.test(fullLower(label))) return bind('Invoice', 'VatAmount');
  if (/(^id$|invoiceid|faturaid)/.test(n)) return bind('Invoice', 'TextId');
  return null;
}

// --- Detay (kalem) kolonu eşlemesi: başlık metni önce, sonra XPath ---
// Sıra ÖNEMLİ: özgül (iskonto/kdv) → genel (tutar) → en sonda ProductName, yoksa
// "Mal Hizmet Tutarı" (satır toplamı) yanlışlıkla Ürün Adı'na düşer.
export function mapDetailColumn(path, header) {
  // Başlık eğik-çizgi ile BÖLÜNMEZ (normLabel); XPath son adımı için norm.
  const candidates = [normLabel(header), norm(path)];
  for (const n of candidates) {
    if (!n) continue;
    if (/^sira|sıra|^no$|sirano|^id$|^line$/.test(n)) return bind('InvoiceDetail', 'UnitOrder');
    if (/itemcode|stokkod|urunkod|ürünkod|malkod|sellersitem|buyersitem|barkod/.test(n)) return bind('InvoiceDetail', 'ProductTextId');
    if (/invoicedquantity|quantity|miktar|adet/.test(n)) return bind('InvoiceDetail', 'Quantity');
    if (/priceamount|birimfiyat|fiyat|price/.test(n) && !/tutar|toplam|amount/.test(n)) return bind('InvoiceDetail', 'UnitPrice');
    // İskonto/indirim: Tutar → DiscountAmount; Oran (tutarsız) → bağlama (kullanıcı seçsin).
    if (/iskonto|indirim|allowance/.test(n)) return /tutar|amount/.test(n) ? bind('InvoiceDetail', 'DiscountAmount') : null;
    if (/kdvoran|kdv ?%/.test(n) || (/kdv/.test(n) && /oran|percent/.test(n))) return bind('InvoiceDetail', 'VatPercentage');
    if (/kdvtutar|vergitutar/.test(n) || (/kdv|vergi|tax/.test(n) && /tutar|amount/.test(n))) return bind('InvoiceDetail', 'VatAmount');
    if (/^percent$|^oran/.test(n)) return bind('InvoiceDetail', 'VatPercentage');
    // Genel tutar/satır toplamı ("Mal Hizmet Tutarı", "Tutar", "Toplam", LineExtensionAmount).
    if (/lineextension|maltutar|malhizmettutar|tutar|toplam|netamount|^amount$/.test(n)) return bind('InvoiceDetail', 'NetAmount');
    // En son: ürün adı (tutar/toplam içermeyen "Mal/Hizmet", "Açıklama", Item/Name).
    if (/itemname|^name$|urunad|ürünad|malhizmet|mal\/hizmet|aciklama|açıklama|cins|^mal$|^item$|^ad$/.test(n)) return bind('InvoiceDetail', 'ProductName');
  }
  return null;
}

function bind(source, key) {
  return { source, fieldKey: key, fieldType: fieldType(source, key) };
}
